import { useRef } from "react";
import { Crosshair, RotateCcw } from "lucide-react";
import { thumb } from "@/lib/api";

const SHAPES = [["portrait", "aspect-[4/5] w-20"], ["square", "aspect-square w-24"], ["landscape", "aspect-[16/10] w-32"]];
const clamp = (v) => Math.round(Math.min(100, Math.max(0, v)));

// Click or drag on the artwork to choose the point that stays visible when it fills a Works tile.
export const FocusPicker = ({ src, x, y, onChange }) => {
  const img = useRef(null);
  const pick = (e) => {
    const r = img.current.getBoundingClientRect();
    onChange(clamp(((e.clientX - r.left) / r.width) * 100), clamp(((e.clientY - r.top) / r.height) * 100));
  };
  const onDown = (e) => { e.preventDefault(); e.currentTarget.setPointerCapture(e.pointerId); pick(e); };
  const onMove = (e) => { if (e.currentTarget.hasPointerCapture(e.pointerId)) pick(e); };
  const pos = `${x}% ${y}%`;

  return (
    <div data-testid="focus-picker" className="bg-[#0B0C10] border border-[#F5A524]/20 p-4 space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="flex items-center gap-2 font-mono text-[11px] tracking-[0.25em] text-[#F5A524]"><Crosshair size={14} /> TILE FOCUS POINT</p>
        <button type="button" data-testid="focus-reset-btn" onClick={() => onChange(50, 50)} className="flex items-center gap-1 font-mono text-[10px] tracking-[0.2em] text-zinc-400 hover:text-[#F5A524]">
          <RotateCcw size={11} /> CENTER
        </button>
      </div>
      <p className="text-xs text-zinc-400">Click or drag on the artwork to choose the part that stays visible in the tile. The full piece still opens uncropped.</p>
      <div className="flex flex-col lg:flex-row gap-5 lg:items-start">
        <div className="relative inline-block self-start cursor-crosshair touch-none select-none" onPointerDown={onDown} onPointerMove={onMove} data-testid="focus-picker-area">
          <img ref={img} src={thumb(src, 900)} alt="Choose focus point" draggable={false} className="block max-h-[360px] max-w-full w-auto" />
          <span className="absolute inset-0 bg-black/30 pointer-events-none" />
          <span data-testid="focus-marker" className="absolute w-7 h-7 -ml-3.5 -mt-3.5 rounded-full border-2 border-[#F5A524] shadow-[0_0_0_2px_rgba(0,0,0,.6),0_0_18px_rgba(245,165,36,.7)] pointer-events-none grid place-items-center" style={{ left: `${x}%`, top: `${y}%` }}>
            <span className="w-1.5 h-1.5 rounded-full bg-[#F5A524]" />
          </span>
        </div>
        <div className="space-y-2">
          <p className="font-mono text-[10px] tracking-[0.25em] text-zinc-400">PREVIEW · <span data-testid="focus-value">{x}% / {y}%</span></p>
          <div className="flex items-end gap-3">
            {SHAPES.map(([id, cls]) => (
              <div key={id} className="space-y-1">
                <div className={`${cls} clip-corner overflow-hidden bg-[#12131A]`}>
                  <img src={thumb(src, 480)} alt="" className="w-full h-full object-cover" style={{ objectPosition: pos }} />
                </div>
                <p className="font-mono text-[9px] tracking-[0.2em] text-zinc-400 uppercase">{id}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};
